import { createFileRoute } from "@tanstack/react-router";
import { Info } from "lucide-react";
import { Page, PageHeader, Section } from "@/app/Page";
import { useProjectSlug, useWorkspaceSlug } from "@/app/context";
import { Card, ErrorState, ExplainPanel, Money, ShowAsCode, Skeleton } from "@/design";
import { useBilling, useInvoices, useProject, useProjectSpend, useRateCard, useSpendCaps, useWorkspaceSpend } from "@/lib/api";
import { docsUrl } from "@/lib/config";
import { moneyAmount } from "@/lib/format";
import { CapCard } from "./-spend/CapCard";
import { buildModel } from "./-spend/model";
import { Breakdown, Invoices, UsageTable } from "./-spend/Sections";
import { SpendHero } from "./-spend/SpendHero";
import { UsageSection } from "./-spend/UsageSection";

export const Route = createFileRoute("/$workspace/$project/spend")({
  staticData: { title: "Spend" },
  component: SpendScreen,
});

function SpendScreen() {
  const workspace = useWorkspaceSlug();
  const project = useProjectSlug();
  const proj = useProject(project);
  const spend = useProjectSpend(project);
  const caps = useSpendCaps(workspace);
  const wsSpend = useWorkspaceSpend(workspace);
  const rateCard = useRateCard();
  const invoices = useInvoices(workspace);
  const billing = useBilling(workspace);

  const error = spend.error ?? caps.error ?? proj.error;
  const ready = spend.data && caps.data && proj.data;
  const m = ready ? buildModel(spend.data, caps.data.data, proj.data.id) : null;
  const name = proj.data?.name ?? project;

  return (
    <Page width="wide">
      <PageHeader
        title="Spend"
        description="What this project costs, why it costs that, and the cap that keeps it in check."
        actions={
          <ShowAsCode
            title="Check spend from your terminal or agent"
            cli={`guv spend --project ${project} --forecast\nguv spend cap list`}
            apiCall={{ method: "GET", path: `/v1/projects/${project}/spend` }}
          />
        }
      />

      {error ? (
        <ErrorState
          error={error}
          onRetry={() => {
            void spend.refetch();
            void caps.refetch();
            void proj.refetch();
          }}
        />
      ) : !m || !spend.data ? (
        <SpendSkeleton />
      ) : (
        <>
          <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]">
            <SpendHero m={m} updatedAt={spend.data.updated_at} workspaceMtd={wsSpend.data ? moneyAmount(wsSpend.data.month_to_date) : undefined} />
            <CapCard m={m} workspace={workspace} project={project} projectName={name} />
          </div>

          <UsageSection className="mt-10" scope={{ type: "project", project }} cap={m.cap} />

          <Section
            className="mt-10"
            title="Where the money goes"
            description="Each service and database this month, with the reason it costs what it does."
            actions={
              <ExplainPanel title="How we work this out" learnMoreUrl={docsUrl("/pricing")} side="left">
                <p>
                  We measure what each service actually uses, every few seconds: processor time at <strong>£10 per vCPU-month</strong> and memory at <strong>£5 per GB-month</strong>. A service
                  that sleeps costs nothing while asleep.
                </p>
                <p>Databases include storage and their daily backups and restore tests.</p>
              </ExplainPanel>
            }
          >
            <Breakdown summary={spend.data} />
          </Section>

          <div className="mt-10">
            <Section title="Meters this month" description="Every meter this month, at the published rates.">
              <UsageTable summary={spend.data} rateCard={rateCard.data} project={project} from={spend.data.period.start} />
              {wsSpend.data?.included_usage && (
                <p className="mt-3 flex items-start gap-2 text-sm text-muted">
                  <Info className="mt-0.5 size-4 shrink-0 text-subtle" />
                  <span>
                    Your plan includes <Money value={wsSpend.data.included_usage} whole className="text-text" /> of usage a month across the workspace, so the first{" "}
                    <Money value={wsSpend.data.included_usage} whole /> of usage is already paid for by your <Money value={wsSpend.data.plan_fee ?? 0} whole /> plan fee.
                  </span>
                </p>
              )}
            </Section>
            <Section className="mt-10" title="Invoices" description="Invoices cover your whole workspace.">
              <Invoices
                invoices={invoices.data?.data}
                isPending={invoices.isPending}
                error={invoices.error}
                onRetry={() => void invoices.refetch()}
                nextEstimate={billing.data?.next_invoice_estimate ? moneyAmount(billing.data.next_invoice_estimate) : undefined}
                workspace={workspace}
              />
            </Section>
          </div>
        </>
      )}
    </Page>
  );
}

function SpendSkeleton() {
  return (
    <div role="status" aria-busy="true" className="grid gap-5 lg:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]">
      <span className="sr-only">Loading spend</span>
      <Card className="p-6">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="mt-4 h-10 w-3/4" />
        <Skeleton className="mt-6 h-2.5 w-full" />
        <Skeleton className="mt-6 h-[190px] w-full" />
      </Card>
      <Card className="p-6">
        <Skeleton className="h-5 w-28" />
        <Skeleton className="mt-4 h-10 w-24" />
        <Skeleton className="mt-6 h-32 w-full" />
      </Card>
    </div>
  );
}
